import {Component, Input, NgZone, ViewChild} from "@angular/core";
import {FormsModule} from "@angular/forms";
import {NgForOf, NgIf, TitleCasePipe} from "@angular/common";
import {AnnualSchoolProfile} from "../interfaces/annual-school-profile";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {UserService} from "../services/user.service";
import firebase from "firebase/compat/app";
import {
  AnnualSchoolProfilesDetailsCardComponent
} from "../annual-school-profiles-details-card/annual-school-profiles-details-card.component";
import {AnnualSchoolProfilesFormComponent} from "../annual-school-profiles-form/annual-school-profiles-form.component";
import {TeacherService} from "../services/teacher.service";
import {Teacher} from "../interfaces/teacher";

@Component({
  selector: 'app-annual-school-profiles-details',
  templateUrl: './annual-school-profiles-details.component.html',
  standalone: true,
  imports: [
    FormsModule,
    NgForOf,
    NgIf,
    TitleCasePipe,
    AnnualSchoolProfilesDetailsCardComponent,
    AnnualSchoolProfilesFormComponent,
    RouterLink
  ],
  styleUrl: './annual-school-profiles-details.component.css'
})
export class AnnualSchoolProfilesDetailsComponent {
  constructor(private route: ActivatedRoute, public userService: UserService, private ngZone: NgZone, private router: Router, private teacherService: TeacherService) { }
  profileId!: string;
  profile!: AnnualSchoolProfile;
  isAdmin = false;
  isEditing = false;
  profileExists = true;
  projects: any[] = [];
  @Input() formData!: AnnualSchoolProfile;
  @ViewChild(AnnualSchoolProfilesFormComponent) formComponent!: AnnualSchoolProfilesFormComponent;
  teacher: Teacher | null = null;
  teacherRole: string | undefined = undefined;

  async ngOnInit() {
    this.teacher = await this.teacherService.getTeacher();
    this.teacherRole = this.teacher?.role;
    this.profileId = this.route.snapshot.paramMap.get("id")!;
    await this.loadProfile();
    await this.getProject();

    this.userService.isAdmin$.subscribe((isAdmin) => {
      this.isAdmin = isAdmin;
    });
  }

  async loadProfile() {
    try {
      const docSnap = await firebase
        .firestore()
        .collection("annualSchoolProfile")
        .doc(this.profileId)
        .get();

      if (docSnap.exists) {
        this.profile = { id: docSnap.id, ...docSnap.data() } as AnnualSchoolProfile;
      } else {
        this.profileExists = false;
      }
    } catch (err) {
      console.error("Error loading profile:", err);
      this.profileExists = false;
    }
  }

  toggleEditing() {
    this.isEditing = !this.isEditing;

    if (this.isEditing) {
      // wait for the form
      setTimeout(() => {
        if (this.formComponent && this.profile) {
          this.formComponent.initializeForm(this.profile);
        }
      }, 0);
    }
  }

  onFormUpdated(updatedProfile: AnnualSchoolProfile) {
    this.profile = updatedProfile;
    this.isEditing = false;
  }

  async getProject() {
    const snapshot = await firebase.firestore().collection('projects').get();
    this.projects = snapshot.docs.map(doc => {
      return { id: doc.id, ...doc.data() };
    });
    this.projects.sort((a: any, b: any) =>
      (a.title || '').localeCompare(b.title || '')
    );

  }

  addProject() {
    this.router.navigate(['/new-project'], {
      queryParams: { profileId: this.profileId, schoolYear: this.profile.schoolYear }
    });
  }

  isDeleting = false;
  deleteProfile() {
    if (!confirm("Are you sure you want to delete this profile?")) return;
    this.isDeleting = true;

    return firebase.firestore()
      .collection('annualSchoolProfile')
      .doc(this.profile.id)
      .set({ isDeleted: true }, { merge: true })
      .then(() => {
        this.profile.isDeleted = true;
        this.isDeleting = false;
      })
      .catch(err => {
        console.error(err);
        this.isDeleting = false;
      });
  }

  undeleteProfile() {
    if (!confirm("Are you sure you want to undelete this profile?")) return;
    this.isDeleting = true;

    return firebase.firestore()
      .collection('annualSchoolProfile')
      .doc(this.profile.id)
      .set({ isDeleted: false }, { merge: true })
      .then(() => {
        this.profile.isDeleted = false;
        this.isDeleting = false;
      })
      .catch(err => {
        console.error(err);
        this.isDeleting = false;
      });
  }

  async duplicateProfile() {
    if (!this.profile) return;

    const keep = [
      'project',
      'school',
      'ghsRepresentativeName',
      'ghsRepresentativeEmail',
      'additionalGhsRepresentativeName',
      'additionalGhsRepresentativeEmail',
      'studentServingOnGreenYouth',
      'goals',
      'createdBy',
      'isWithinCityLimits',
    ];

    const draftProfile: any = {};

    for (const key in this.profile) {
      if (!keep.includes(key)) continue;

      const value = (this.profile as any)[key];

      if (key === 'project' && Array.isArray(value)) {
        draftProfile[key] = value.map((ref: any) => ref.id); // store ids
      } else {
        draftProfile[key] = value;
      }
    }

    await this.router.navigate(['/annual-school-profiles-form'], {state: {draftProfile}});
  }
}
